import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { useTranslation, Trans } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Users, CheckCircle, TrendingUp, Mail, Flame, Search, MailX, Clock, ShieldCheck, X, CalendarCheck, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@tanstack/react-router";

const BURN_SECONDS = 300; // must match activate-redeem edge function
import { toast } from "sonner";
import { approveFeedback, rejectFeedback } from "@/lib/feedback.functions";
import { CashierManualRedeem } from "@/components/CashierManualRedeem";
import { withAuth } from "@/lib/server-fn-auth";
import { CURRENCY_SYMBOLS, type Currency } from "@/lib/onboarding-defaults";
import { TrialStatusBanner } from "@/components/TrialStatusBanner";
import { TrialActivationBanner } from "@/components/TrialActivationBanner";
import { DashboardReferralCta } from "@/components/DashboardReferralCta";
import { UsageWidget } from "@/components/UsageWidget";
import { ProductFeedbackBanner } from "@/components/ProductFeedbackBanner";
import { SendFeedbackDialog } from "@/components/SendFeedbackDialog";
import { Sentry } from "@/lib/sentry";
import { AnonymizeFeedbackButton } from "@/components/AnonymizeFeedbackButton";
import { maskPhoneDisplay, formatFeedbackTime } from "@/lib/contact-display";
import type { Tables } from "@/integrations/supabase/types";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});

type Merchant = Tables<"merchants">;
type FeedbackLog = Tables<"feedback_logs">;
type NudgeLog = Tables<"nudge_log">;

function formatMMSS(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function maskEmail(email: string | null): string {
  if (!email) return "—";
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  return local[0] + "***@" + domain;
}

function ActiveCodesPanel({ feedback, redemptionStyle }: { feedback: FeedbackLog[]; redemptionStyle: string }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());

  // Tick every second so countdowns update live
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const activeCodes = useMemo(() => {
    return feedback.filter((f) => {
      if (f.status === "voucher_active") return true; // verified voucher — no timer
      if (f.status !== "redeeming") return false;
      // Quick burn — only show while timer hasn't elapsed
      if (!f.redeemed_started_at) return true;
      const elapsed = (now - new Date(f.redeemed_started_at).getTime()) / 1000;
      return elapsed < BURN_SECONDS;
    });
  }, [feedback, now]);

  if (activeCodes.length === 0) return null;

  return (
    <Card className="mb-8 border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Flame size={18} className="text-primary" />
          {redemptionStyle === "verified_voucher" ? t("dashboard.activeVouchers") : t("dashboard.activeCodes")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {activeCodes.map((f) => {
            const isBurning = f.status === "redeeming" && f.redeemed_started_at;
            const remaining = isBurning
              ? Math.max(0, Math.ceil(BURN_SECONDS - (now - new Date(f.redeemed_started_at!).getTime()) / 1000))
              : 0;
            return (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                <div className="flex items-center gap-3">
                  <code className="text-sm font-bold tracking-widest bg-primary/10 text-primary px-2 py-1 rounded">{f.promo_code}</code>
                  <span className="text-xs text-muted-foreground">{maskEmail(f.customer_email)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {isBurning && (
                    <span className={`text-xs font-mono tabular-nums flex items-center gap-1 ${remaining <= 30 ? "text-destructive" : "text-muted-foreground"}`}>
                      <Clock size={12} />
                      {formatMMSS(remaining)}
                    </span>
                  )}
                  <Badge variant="default" className="text-xs bg-primary/80">
                    {f.status === "redeeming" ? t("dashboard.burning") : t("dashboard.active")}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function UsedTodayPanel({ feedback }: { feedback: FeedbackLog[] }) {
  const { t } = useTranslation();
  const usedToday = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    // Include both fully-redeemed (verified voucher) AND in-progress burns
    // (quick burn — the customer already showed the cashier the burning code,
    // which IS the redemption event in that mode).
    return feedback
      .filter((f) => {
        const ts = f.redeemed_at ?? f.redeemed_started_at;
        if (!ts) return false;
        if (f.status !== "redeemed" && f.status !== "redeeming") return false;
        return new Date(ts) >= startOfDay;
      })
      .sort((a, b) => {
        const at = new Date(a.redeemed_at ?? a.redeemed_started_at!).getTime();
        const bt = new Date(b.redeemed_at ?? b.redeemed_started_at!).getTime();
        return bt - at;
      });
  }, [feedback]);

  if (usedToday.length === 0) return null;

  return (
    <Card className="mb-8 border-tack-green/30">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <CheckCircle size={18} className="text-tack-green" />
          {t("dashboard.usedToday")}
          <span className="text-xs font-normal text-muted-foreground ml-1">({usedToday.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {usedToday.map((f) => {
            const ts = f.redeemed_at ?? f.redeemed_started_at!;
            return (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                <div className="flex items-center gap-3">
                  <code className="text-sm font-bold tracking-widest bg-tack-green/10 text-tack-green px-2 py-1 rounded">{f.promo_code}</code>
                  <span className="text-xs text-muted-foreground">{maskEmail(f.customer_email)}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PendingApprovalPanel({
  pendingFeedback,
  onApproved,
  onRejected,
  showLocationBadge = false,
  merchantNameById,
}: {
  /** Dedicated pending list, loaded unbounded — not filtered from the capped recent-feedback array. */
  pendingFeedback: FeedbackLog[];
  onApproved: (id: string) => void;
  onRejected: (id: string) => void;
  /** In aggregated ("all locations") view, label each row with its location. */
  showLocationBadge?: boolean;
  merchantNameById?: Map<string, string>;
}) {
  const { t, i18n } = useTranslation();
  const [busyId, setBusyId] = useState<string | null>(null);

  const pending = useMemo(
    () =>
      pendingFeedback
        .filter((f: any) => f.approval_status === "pending_approval")
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [pendingFeedback]
  );

  if (pending.length === 0) return null;

  const handleApprove = async (id: string) => {
    setBusyId(id);
    try {
      await withAuth(approveFeedback)({ data: { feedbackId: id } });
      toast.success(t("dashboard.toast.approvedSent"));
      onApproved(id);
    } catch {
      toast.error(t("dashboard.toast.approveFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id: string) => {
    setBusyId(id);
    try {
      await withAuth(rejectFeedback)({ data: { feedbackId: id } });
      toast.success(t("dashboard.toast.discarded"));
      onRejected(id);
    } catch {
      toast.error(t("dashboard.toast.rejectFailed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="mb-8 border-tack-amber/40">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <ShieldCheck size={18} className="text-tack-amber" />
          {t("dashboard.pendingApproval")}
          <span className="text-xs font-normal text-muted-foreground ml-1">({pending.length})</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {t("dashboard.pendingApprovalHint")}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pending.map((f) => (
            <div key={f.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-border/50 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className={i < f.star_rating ? "text-tack-amber fill-tack-amber" : "text-muted-foreground/20"}
                      />
                    ))}
                  </div>
                  {(f.phone || f.customer_email) && (
                    <span className="text-sm text-muted-foreground truncate font-mono">
                      {f.phone ? maskPhoneDisplay(f.phone) : f.customer_email}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatFeedbackTime(f.created_at, i18n.language)}
                  </span>
                  {showLocationBadge && merchantNameById?.get(f.merchant_id) && (
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      {merchantNameById.get(f.merchant_id)}
                    </Badge>
                  )}
                </div>
                {f.customer_comment && (
                  <p className="text-xs text-muted-foreground italic line-clamp-2">"{f.customer_comment}"</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReject(f.id)}
                  disabled={busyId === f.id}
                >
                  <X size={14} /> {t("dashboard.discard")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleApprove(f.id)}
                  disabled={busyId === f.id}
                >
                  <CheckCircle size={14} /> {t("dashboard.approve")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { current, locations, viewMode, loading: locationLoading } = useCurrentLocation();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [feedback, setFeedback] = useState<FeedbackLog[]>([]);
  // Pending approvals get their own unbounded query — the panel must show every
  // item the nav bubble counts, not just those among the 50 newest entries.
  const [pendingFeedback, setPendingFeedback] = useState<FeedbackLog[]>([]);
  const [nudges, setNudges] = useState<NudgeLog[]>([]);
  const [unsubscribedCount, setUnsubscribedCount] = useState(0);
  const [bookingClicks, setBookingClicks] = useState(0);
  const [loading, setLoading] = useState(true);

  // In aggregated mode, every owner-location is in scope. In single mode,
  // only the currently selected location.
  const ownedLocationIds = useMemo(
    () => locations.filter((l) => l.role === "owner").map((l) => l.id),
    [locations]
  );
  const isAggregated = viewMode === "all" && ownedLocationIds.length > 1;
  const scopedMerchantIds = useMemo(
    () => (isAggregated ? ownedLocationIds : current?.id ? [current.id] : []),
    [isAggregated, ownedLocationIds, current?.id]
  );
  // Stable join key for effect dependency — array identity changes every render.
  const scopedKey = scopedMerchantIds.join(",");
  // Map merchant_id → display name for per-row chips in aggregated mode.
  const merchantNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const loc of locations) m.set(loc.id, loc.business_name);
    return m;
  }, [locations]);

  // One-time toast after returning from Stripe checkout. Strips the param so
  // it doesn't fire again on refresh.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success(t("dashboard.subscriptionActive"));
      params.delete("checkout");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : "")
      );
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (locationLoading) return;

    // No merchant rows at all → onboarding.
    if (scopedMerchantIds.length === 0) {
      setMerchant(null);
      setLoading(false);
      return;
    }

    // The "canonical" merchant row used for currency, redemption_style, slug,
    // poster CTAs, etc. In aggregated mode that's the default location; in
    // single mode it's the selected location.
    const canonicalId = isAggregated
      ? (locations.find((l) => l.is_default)?.id ?? scopedMerchantIds[0])
      : current!.id;

    async function loadData() {
      const { data: merchantData } = await supabase
        .from("merchants")
        .select("*")
        .eq("id", canonicalId)
        .maybeSingle();

      if (merchantData) {
        if (!merchantData.onboarding_completed) {
          navigate({ to: "/onboarding", search: { rerun: undefined } });
          return;
        }
        setMerchant(merchantData);
        Sentry.setTag("merchant_slug", merchantData.slug);

        const { data: feedbackData } = await supabase
          .from("feedback_logs")
          .select("*")
          .in("merchant_id", scopedMerchantIds)
          .order("created_at", { ascending: false })
          .limit(50);

        setFeedback(feedbackData || []);

        const { data: pendingData } = await supabase
          .from("feedback_logs")
          .select("*")
          .eq("approval_status", "pending_approval")
          .in("merchant_id", scopedMerchantIds)
          .order("created_at", { ascending: false })
          .limit(200);
        setPendingFeedback(pendingData || []);

        const { data: nudgeData } = await supabase
          .from("nudge_log")
          .select("*")
          .in("merchant_id", scopedMerchantIds)
          .order("sent_at", { ascending: false })
          .limit(50);

        setNudges(nudgeData || []);

        const { count: unsubCount } = await supabase
          .from("email_unsubscribes")
          .select("*", { count: "exact", head: true })
          .in("merchant_id", scopedMerchantIds);

        setUnsubscribedCount(unsubCount || 0);

        const { count: clickCount } = await supabase
          .from("analytics_events")
          .select("*", { count: "exact", head: true })
          .in("merchant_id", scopedMerchantIds)
          .eq("event_type", "booking_link_click");
        setBookingClicks(clickCount || 0);
      } else {
        setMerchant(null);
      }
      setLoading(false);
    }

    setLoading(true);
    loadData();
  }, [user, scopedKey, locationLoading]);

  // Realtime subscription. In single-location mode the postgres_changes
  // filter scopes server-side. In aggregated mode we'd need an "in" filter,
  // which postgres_changes doesn't support — so we open one channel per
  // scoped merchant id (≤5 in practice; gated by Growth cap).
  useEffect(() => {
    if (scopedMerchantIds.length === 0) return;

    const channels = scopedMerchantIds.map((mid) =>
      supabase
        .channel(`feedback-realtime-${mid}-${Math.random().toString(36).slice(2, 8)}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "feedback_logs",
            filter: `merchant_id=eq.${mid}`,
          },
          (payload) => {
            if (payload.eventType === "INSERT") {
              setFeedback((prev) => [payload.new as FeedbackLog, ...prev]);
              if ((payload.new as FeedbackLog).approval_status === "pending_approval") {
                setPendingFeedback((prev) => [payload.new as FeedbackLog, ...prev]);
              }
              toast.info(t("dashboard.toast.newFeedback"));
            } else if (payload.eventType === "UPDATE") {
              const next = payload.new as FeedbackLog;
              const prevRow = payload.old as Partial<FeedbackLog> | undefined;
              setFeedback((prev) =>
                prev.map((f) => (f.id === next.id ? next : f))
              );
              setPendingFeedback((prev) =>
                next.approval_status === "pending_approval"
                  ? prev.some((f) => f.id === next.id)
                    ? prev.map((f) => (f.id === next.id ? next : f))
                    : [next, ...prev]
                  : prev.filter((f) => f.id !== next.id)
              );
              const becameRedeemed =
                next.status === "redeemed" && prevRow?.status !== "redeemed";
              const becameBurning =
                next.status === "redeeming" && prevRow?.status !== "redeeming";
              if (becameRedeemed) {
                toast.success(t("dashboard.toast.codeRedeemed"));
              } else if (becameBurning) {
                toast.success(t("dashboard.toast.customerAtCounter"));
              }
            }
          }
        )
        .subscribe()
    );

    return () => {
      for (const ch of channels) supabase.removeChannel(ch);
    };
  }, [scopedKey]);

  // Loyal regulars: phones that have hit the merchant's reward quota in the
  // current window. Must run before any early return to keep hook order stable.
  const quotaCount = (merchant as any)?.reward_quota_count ?? 0;
  const quotaWindowDays = (merchant as any)?.reward_quota_window_days ?? 60;
  const loyalRegularPhones = useMemo(() => {
    const set = new Set<string>();
    if (!quotaCount || quotaCount <= 0) return set;
    const cutoff = Date.now() - quotaWindowDays * 24 * 60 * 60 * 1000;
    const counts = new Map<string, number>();
    for (const f of feedback) {
      if (!f.phone || !f.redeemed_at) continue;
      if (new Date(f.redeemed_at).getTime() < cutoff) continue;
      counts.set(f.phone, (counts.get(f.phone) ?? 0) + 1);
    }
    for (const [phone, n] of counts) {
      if (n >= quotaCount) set.add(phone);
    }
    return set;
  }, [feedback, quotaCount, quotaWindowDays]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("dashboard.loading")}</div>
      </div>
    );
  }


  if (!merchant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-sm w-full">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground mb-4">{t("dashboard.noMerchant")}</p>
            <Button
              onClick={async () => {
                await supabase.auth.signOut();
                navigate({ to: "/" });
              }}
            >
              {t("dashboard.signOut")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalFeedback = feedback.length;
  const avgRating = totalFeedback > 0
    ? (feedback.reduce((sum, f) => sum + f.star_rating, 0) / totalFeedback).toFixed(1)
    : "0";
  // Redemption semantics depend on the merchant's redemption style:
  //   - verified_voucher: cashier taps "Apply & Redeem" → status='redeemed'
  //   - quick_burn: customer shows the burning code to the cashier → status='redeeming'
  //     (there is no later confirmation step — the burn IS the redemption)
  // We count both terminal-success states so the counter is accurate in both modes.
  const isQuickBurn = (merchant.redemption_style ?? "quick_burn") === "quick_burn";
  const redemptions = feedback.filter((f) =>
    f.status === "redeemed" || (isQuickBurn && f.status === "redeeming")
  ).length;
  const discountPct = merchant.promo_discount_percent ?? 0;
  const netMultiplier = Math.max(0, 1 - discountPct / 100);
  const estimatedRevenue = Math.round(redemptions * (merchant.avg_order_value ?? 500) * netMultiplier);
  const totalNudges = nudges.filter((n) => n.status === "sent").length;
  const nudgesThisMonth = nudges.filter((n) => {
    const d = new Date(n.sent_at);
    const now = new Date();
    return n.status === "sent" && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;




  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <TrialActivationBanner />

        <TrialStatusBanner />
        {/* DashboardReferralCta moved to the bottom of the page */}
        <ProductFeedbackBanner />

        {isAggregated && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="p-4 text-sm flex items-start gap-3">
              <CheckCircle size={16} className="text-primary mt-0.5 shrink-0" />
              <div>
                <Trans
                  i18nKey="dashboard.aggregated"
                  values={{ count: ownedLocationIds.length }}
                  components={{ 1: <strong /> }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        {(() => {
          const aov = merchant?.avg_order_value ?? 500;
          const netPct = Math.round(netMultiplier * 100);
          const currencySymbol = CURRENCY_SYMBOLS[(merchant?.currency || "SEK") as Currency] || "kr";
          const bookingStatus = (merchant as any).booking_url_status as string | null;
          const hasApprovedBooking = !!(merchant as any).booking_url && bookingStatus === "approved";
          const bookingPending = !!(merchant as any).booking_url && bookingStatus === "pending";
          const bookingRejected = !!(merchant as any).booking_url && bookingStatus === "rejected";

          return (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {/* Total feedback */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t("dashboard.stats.totalFeedback")}</span>
                  </div>
                  <p className="text-2xl font-display font-bold">{totalFeedback}</p>
                </CardContent>
              </Card>

              {/* Avg rating */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t("dashboard.stats.avgRating")}</span>
                  </div>
                  <p className="text-2xl font-display font-bold">{avgRating} ★</p>
                </CardContent>
              </Card>

              {/* Redemptions */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t("dashboard.stats.redemptions")}</span>
                  </div>
                  <p className="text-2xl font-display font-bold">{redemptions}</p>
                </CardContent>
              </Card>

              {/* Estimated revenue with tooltip */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t("dashboard.stats.estRevenue")}</span>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" aria-label={t("dashboard.stats.estRevenueInfoAria")} className="text-muted-foreground/70 hover:text-foreground transition-colors">
                            <Info size={13} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
                          <p className="mb-1 font-medium">{t("dashboard.stats.estRevenueTooltipTitle")}</p>
                          <p className="text-muted-foreground">
                            {t("dashboard.stats.estRevenueFormula", {
                              aov,
                              netPct,
                              redemptions,
                              currency: currencySymbol,
                              result: estimatedRevenue,
                              discountPct,
                            })}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-2xl font-display font-bold">{estimatedRevenue} {currencySymbol}</p>
                </CardContent>
              </Card>

              {/* Booking clicks — greyed out when no approved booking URL */}
              {hasApprovedBooking ? (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarCheck size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{t("dashboard.stats.bookingClicks")}</span>
                    </div>
                    <p className="text-2xl font-display font-bold">{bookingClicks}</p>
                    {redemptions > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {Math.round((bookingClicks / redemptions) * 100)}% {t("dashboard.bookingClicks.ctr")}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-muted/30 border-dashed">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarCheck size={14} className="text-muted-foreground/60" />
                      <span className="text-xs text-muted-foreground">{t("dashboard.stats.bookingClicks")}</span>
                    </div>
                    <p className="text-2xl font-display font-bold text-muted-foreground/60">—</p>
                    {bookingPending ? (
                      <p className="text-[11px] text-muted-foreground mt-0.5">{t("dashboard.stats.bookingPending")}</p>
                    ) : bookingRejected ? (
                      <Link to="/settings" className="text-[11px] text-primary hover:underline mt-0.5 inline-block">
                        {t("dashboard.stats.bookingRejectedCta")}
                      </Link>
                    ) : (
                      <Link to="/settings" className="text-[11px] text-primary hover:underline mt-0.5 inline-block">
                        {t("dashboard.stats.bookingAddCta")}
                      </Link>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          );
        })()}

        {/* Plan usage — hidden for Enterprise / unlimited */}
        <UsageWidget />




        {/* ROI Banner */}
        {redemptions > 0 && (
          <Card className="mb-8 border-tack-green/30 bg-tack-green/5">
            <CardContent className="p-4">
              <p className="text-sm font-medium">
                {t("dashboard.roi")} <span className="text-lg font-bold text-tack-green">{Math.round((estimatedRevenue / (79 * 1)) * 100)}%</span>
                <span className="text-muted-foreground ml-2">{t("dashboard.roiSuffix", { count: redemptions })}</span>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Active Codes Panel (Quick Burn — passive verification).
            Hidden in aggregated mode — redemption is location-specific. */}
        {!isAggregated && (
          <ActiveCodesPanel feedback={feedback} redemptionStyle={(merchant as any).redemption_style || "quick_burn"} />
        )}

        {/* Codes Used Today */}
        <UsedTodayPanel feedback={feedback} />

        {/* Manual redemption — available for every redemption style (dead phone,
            forgotten code). Collapsed by default; per-location only. */}
        {!isAggregated && (
          <div className="mb-8">
            <CashierManualRedeem
              redemptionStyle={(merchant as any).redemption_style || "quick_burn"}
              merchantId={merchant.id}
              collapsible
            />
          </div>
        )}

        {/* Pending Approval. Single-location view: only for the voucher style.
            Aggregated view: whenever any location has rows awaiting approval —
            such rows only exist for voucher-style locations anyway. Each row is
            labelled with its location so it can be approved from here. */}
        {(isAggregated || (merchant as any).redemption_style === "verified_voucher") && (
          <PendingApprovalPanel
            pendingFeedback={pendingFeedback}
            showLocationBadge={isAggregated}
            merchantNameById={merchantNameById}
            onApproved={(id) =>
              setPendingFeedback((prev) => prev.filter((f) => f.id !== id))
            }
            onRejected={(id) =>
              setPendingFeedback((prev) => prev.filter((f) => f.id !== id))
            }
          />
        )}

        {/* Nudge Emails Stats */}
        {totalNudges > 0 && (
          <Card className="mb-8">
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Mail size={18} /> {t("dashboard.nudgeEmails")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6 mb-4">
                <div>
                  <p className="text-2xl font-display font-bold">{totalNudges}</p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.totalSent")}</p>
                </div>
                <div>
                  <p className="text-2xl font-display font-bold">{nudgesThisMonth}</p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.thisMonth")}</p>
                </div>
                <div>
                  <p className="text-2xl font-display font-bold flex items-center gap-1.5">
                    <MailX size={18} className="text-muted-foreground" />
                    {unsubscribedCount}
                  </p>
                  <p className="text-xs text-muted-foreground">{t("dashboard.unsubscribed")}</p>
                </div>
              </div>
              {nudges.slice(0, 5).map((n) => (
                <div key={n.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0 text-sm">
                  <span className="text-muted-foreground">{n.customer_email}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant={n.status === "sent" ? "default" : "secondary"} className="text-xs">{n.status}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(n.sent_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Unsubscribed counter (standalone — shown when no nudge card yet but there are unsubs) */}
        {totalNudges === 0 && unsubscribedCount > 0 && (
          <Card className="mb-8">
            <CardContent className="p-4 flex items-center gap-3">
              <MailX size={18} className="text-muted-foreground" />
              <div>
                <p className="text-2xl font-display font-bold leading-tight">{unsubscribedCount}</p>
                <p className="text-xs text-muted-foreground">{t("dashboard.unsubscribedFrom")}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="font-display text-lg">{t("dashboard.recentFeedback")}</CardTitle>
              {(() => {
                const outstandingCount = feedback.filter((f) =>
                  ["pending", "sent", "voucher_active", "redeeming"].includes(f.status)
                  && (!f.promo_expires_at || new Date(f.promo_expires_at) > new Date())
                ).length;
                return (
                  <Badge variant="secondary" className="gap-1.5 font-normal">
                    <Clock size={12} className="text-primary" />
                    <span className="tabular-nums font-semibold">{outstandingCount}</span>
                    <span className="text-muted-foreground">{outstandingCount === 1 ? t("dashboard.outstandingCode") : t("dashboard.outstandingCodes")}</span>
                  </Badge>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent>
            {feedback.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Star size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t("dashboard.noFeedback")}</p>
                <p className="text-xs mt-1">
                  {t("dashboard.yourFeedbackPage")}{" "}
                  <code className="bg-muted px-2 py-0.5 rounded text-xs">
                    /tack/{merchant.slug}
                  </code>
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedback.map((f) => (
                  <div key={f.id} className="py-3 border-b border-border/50 last:border-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex shrink-0">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={i < f.star_rating ? "text-tack-amber fill-tack-amber" : "text-muted-foreground/20"}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground truncate">{f.customer_email}</span>
                        {isAggregated && merchantNameById.get(f.merchant_id) && (
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {merchantNameById.get(f.merchant_id)}
                          </Badge>
                        )}
                        {f.phone && loyalRegularPhones.has(f.phone) && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] shrink-0 bg-tack-warm text-foreground"
                            title={t("dashboard.regularTitle")}
                          >
                            {t("dashboard.regularBadge")}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <code className="text-xs bg-muted px-2 py-0.5 rounded">{f.promo_code}</code>
                        {f.promo_expires_at && f.status !== "redeemed" && (
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {new Date(f.promo_expires_at) < new Date()
                              ? t("dashboard.feedbackStatus.expired")
                              : `${t("dashboard.feedbackStatus.expShort")} ${new Date(f.promo_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
                          </Badge>
                        )}
                        <Badge variant={f.status === "redeemed" ? "default" : "secondary"} className="text-xs">
                          {t(`dashboard.feedbackStatus.${f.status}`, { defaultValue: f.status })}
                        </Badge>
                        <AnonymizeFeedbackButton
                          feedbackId={f.id}
                          customerEmail={f.customer_email}
                          onAnonymized={(id) =>
                            setFeedback((prev) =>
                              prev.map((row) =>
                                row.id === id
                                  ? { ...row, customer_email: "deleted@anonymized", customer_comment: null }
                                  : row
                              )
                            )
                          }
                        />
                      </div>
                    </div>
                    {f.customer_comment && (
                      <p
                        className="mt-1.5 ml-[88px] text-xs text-muted-foreground italic line-clamp-3"
                        title={f.customer_comment}
                      >
                        "{f.customer_comment}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-8">
          <DashboardReferralCta />
        </div>

        <div className="mt-8 flex justify-center">
          <SendFeedbackDialog />
        </div>
      </div>
    </div>
  );
}
